import { computed, observable } from 'mobx'

import {
  IAddressBounds,
  IGeoJson2DGeographicCoordinates,
  IGeoJson2DGeographicCoordinatesInput,
  IGlobeView,
  IGlobeViewCircle,
  IGlobeViewCircleInput,
  IGlobeViewItemShapeInterface,
  IGlobeViewMapFile,
  IGlobeViewPin,
  IGlobeViewPinInput,
  IGlobeViewPolyline,
  IGlobeViewPolylineInput,
  IGlobeViewRectangle,
  IGlobeViewRectangleInput,
  IGlobeViewSpecificItemData,
  IGlobeViewSpecificItemDataCircleInput,
  IGlobeViewSpecificItemDataPolylineInput,
  IGlobeViewSpecificItemDataRectangleInput,
  IGlobeViewStyle,
  IGlobeViewTextBox,
  IGlobeViewTextBoxInput,
  ILatLng,
  MapFileType,
  SitemapItemShapeType,
} from '~/client/graph'
import Guard from '~/client/src/shared/utils/Guard'

import { copyObjectArray } from '../utils/util'
import BaseModel from './BaseModel'

interface ISitemapGeoPosition {
  latitude: number
  longitude: number
  zoom: number
  bearing?: number
  pitch?: number
  altitude?: number
  bounds?
}

export default class GlobeView
  extends BaseModel<IGlobeView>
  implements IGlobeView
{
  public static fromDto(dto: IGlobeView) {
    return new GlobeView(
      dto.id,
      dto.name,
      dto.projectId,
      dto.items || [],
      dto.mapFiles || [],
      dto.createdAt,
      dto.updatedAt,
      dto.bounds,
      dto.center,
      dto.zoom,
      dto.altitude,
      dto.pitch,
      dto.bearing,
      dto.geoCorners,
      true,
      dto.filledImage,
      dto.style,
    )
  }

  @observable public items: IGlobeViewSpecificItemData[] = []
  @observable public mapFiles: IGlobeViewMapFile[] = []

  public constructor(
    id: string,
    public name: string,
    public projectId: string,
    items: IGlobeViewSpecificItemData[] = [],
    mapFiles: IGlobeViewMapFile[] = [],
    createdAt: number = 0,
    updatedAt: number = 0,
    public bounds?: IAddressBounds,
    public center?: ILatLng,
    public zoom?: number,
    public altitude?: number,
    public pitch?: number,
    public bearing?: number,
    public geoCorners?: IGeoJson2DGeographicCoordinates[],
    public isOrientationLocked?: boolean,
    public filledImage?: string,
    public style?: IGlobeViewStyle,
  ) {
    super(id)

    this.items = copyObjectArray(items)
    this.mapFiles = mapFiles.slice()
    this.setCreatedAt(createdAt)
    this.setUpdatedAt(updatedAt)
    Guard.requireAll({ name, projectId })
  }

  public setGeoposition(
    geoposition: ISitemapGeoPosition,
    bounds?: IGeoJson2DGeographicCoordinatesInput[],
  ) {
    // TODO: remove with back changes
    this.bounds = {
      ne: {
        lat: 0,
        lng: 0,
      },
      sw: {
        lat: 0,
        lng: 0,
      },
    }
    this.center = {
      lat: geoposition.latitude,
      lng: geoposition.longitude,
    }
    this.zoom = geoposition.zoom
    this.altitude = 0
    this.pitch = geoposition.pitch
    this.bearing = geoposition.bearing
    this.geoCorners = bounds
  }

  public isNameEqual(name: string) {
    return this.name === name
  }

  public setName(name: string) {
    this.name = name
  }

  @computed
  public get itemsMap(): { [key: string]: boolean } {
    return (
      this.items?.reduce(
        (acc, item) => {
          acc[item.sitemapItemId] = true
          return acc
        },
        {} as { [key: string]: boolean },
      ) || {}
    )
  }

  @computed
  public get displayDataMap(): {
    [key: string]: { item: IGlobeViewSpecificItemData; idx: number }
  } {
    return (
      this.items?.reduce(
        (acc, item, idx) => {
          acc[item.sitemapItemId] = {
            item: {
              id: item.id,
              sitemapItemId: item.sitemapItemId,
              isHidden: item.isHidden,
              label: item.label,
              shape: item.shape,
              icon: item.icon,
              projectId: item.projectId,
              globeViewId: item.globeViewId,
            },
            idx,
          }
          return acc
        },
        {} as {
          [key: string]: { item: IGlobeViewSpecificItemData; idx: number }
        },
      ) || {}
    )
  }

  public get sitemapIds(): string[] {
    return this.mapFiles
      .filter(f => f.entityType === MapFileType.Sitemap)
      .map(s => s.entityId)
  }

  public get tilesetIds(): string[] {
    return this.mapFiles
      .filter(f => f.entityType === MapFileType.Tileset)
      .map(s => s.entityId)
  }

  public get remoteTilesetIds(): string[] {
    return this.mapFiles
      .filter(f => f.entityType === MapFileType.RemoteTileset)
      .map(s => s.entityId)
  }

  @computed
  public get sitemapsMap(): { [key: string]: boolean } {
    return (
      this.sitemapIds?.reduce(
        (acc, item) => {
          acc[item] = true
          return acc
        },
        {} as { [key: string]: boolean },
      ) || {}
    )
  }

  @computed
  public get tilesetsMap(): { [key: string]: boolean } {
    return (
      this.tilesetIds?.reduce(
        (acc, item) => {
          acc[item] = true
          return acc
        },
        {} as { [key: string]: boolean },
      ) || {}
    )
  }

  @computed
  public get remoteTilesetsMap(): { [key: string]: boolean } {
    return (
      this.remoteTilesetIds?.reduce(
        (acc, item) => {
          acc[item] = true
          return acc
        },
        {} as { [key: string]: boolean },
      ) || {}
    )
  }

  public isItemDisplayed = (sitemapItemId: string): boolean =>
    GlobeView.isItemDisplayedIn(this.displayDataMap, sitemapItemId)

  // Nothing observes displayDataMap off-screen, so a pass over every item reads it once through
  // here rather than rebuilding the whole map per item.
  public static isItemDisplayedIn(
    displayDataMap: GlobeView['displayDataMap'],
    sitemapItemId: string,
  ): boolean {
    const item = displayDataMap?.[sitemapItemId]?.item
    return !!item && !item.isHidden
  }

  public static getItemsByShapeType(
    items: IGlobeViewSpecificItemData[],
    shapeType: SitemapItemShapeType,
  ): IGlobeViewSpecificItemData[] {
    return items?.filter(({ shape }) => shape?.type === shapeType)
  }

  public static getPolylineItems(
    items: IGlobeViewSpecificItemData[],
  ): IGlobeViewSpecificItemData[] {
    return GlobeView.getItemsByShapeType(items, SitemapItemShapeType.Polyline)
  }

  public static getRectangleItems(
    items: IGlobeViewSpecificItemData[],
  ): IGlobeViewSpecificItemData[] {
    return GlobeView.getItemsByShapeType(items, SitemapItemShapeType.Rectangle)
  }

  public static getCircleItems(
    items: IGlobeViewSpecificItemData[],
  ): IGlobeViewSpecificItemData[] {
    return GlobeView.getItemsByShapeType(items, SitemapItemShapeType.Circle)
  }

  public static getItemsWithoutShape(
    items: IGlobeViewSpecificItemData[],
  ): IGlobeViewSpecificItemData[] {
    return items?.filter(i => !i.shape)
  }

  public static getPolylineItemDtos(
    items: IGlobeViewSpecificItemData[],
  ): IGlobeViewSpecificItemDataPolylineInput[] {
    return GlobeView.convertItemsToDtos<IGlobeViewPolylineInput>(
      [
        ...GlobeView.getPolylineItems(items),
        ...GlobeView.getItemsWithoutShape(items),
      ],
      GlobeView.convertShapeToPolylineDto,
    )
  }

  public static getRectangleItemDtos(
    items: IGlobeViewSpecificItemData[],
  ): IGlobeViewSpecificItemDataRectangleInput[] {
    return GlobeView.convertItemsToDtos<IGlobeViewRectangleInput>(
      GlobeView.getRectangleItems(items),
      GlobeView.convertShapeToRectangleDto,
    )
  }

  public static getCircleItemDtos(
    items: IGlobeViewSpecificItemData[],
  ): IGlobeViewSpecificItemDataCircleInput[] {
    return GlobeView.convertItemsToDtos<IGlobeViewCircleInput>(
      GlobeView.getCircleItems(items),
      GlobeView.convertShapeToCircleDto,
    )
  }

  public hasDisplayData = (sitemapItemId: string): boolean => {
    return !!this.displayDataMap?.[sitemapItemId]?.item
  }

  public getItemDisplayData = (
    sitemapItemId: string,
  ): IGlobeViewSpecificItemData => {
    return (
      this.displayDataMap?.[sitemapItemId]?.item ||
      ({} as IGlobeViewSpecificItemData)
    )
  }

  public setItemDisplayData(
    sitemapItemId: string,
    data: IGlobeViewSpecificItemData,
  ) {
    const objIndex = this.findItemIndexBySitemapItemId(sitemapItemId)

    if (objIndex === -1) {
      this.items.push(data)
      return
    }

    this.items.splice(objIndex, 1, data)
  }

  public removeItemDisplayData(id: string) {
    const objIndex = this.findItemIndexByGlobeViewItemDataId(id)
    if (objIndex === -1) {
      return
    }
    this.items.splice(objIndex, 1)
  }

  private findItemIndexBySitemapItemId(itemId: string): number {
    return this.items?.findIndex(
      ({ sitemapItemId }) => sitemapItemId === itemId,
    )
  }

  private findItemIndexByGlobeViewItemDataId(itemId: string): number {
    return this.items?.findIndex(({ id }) => id === itemId)
  }

  private static convertShapeToPolylineDto(
    shape: IGlobeViewPolyline,
  ): IGlobeViewPolylineInput {
    if (!shape) {
      return null
    }

    return {
      lineWidth: shape.lineWidth,
      lineColor: shape.lineColor,
      fillColor: shape.fillColor,
      fillOpacity: shape.fillOpacity,
      type: shape.type,
      isClosed: shape.isClosed,
      arrowPosition: shape.arrowPosition,
      isDisplayed: shape.isDisplayed,
    }
  }

  private static convertShapeToRectangleDto(
    shape: IGlobeViewRectangle,
  ): IGlobeViewRectangleInput {
    if (!shape) {
      return null
    }

    return {
      lineWidth: shape.lineWidth,
      lineColor: shape.lineColor,
      fillColor: shape.fillColor,
      fillOpacity: shape.fillOpacity,
      type: shape.type,
      isDisplayed: shape.isDisplayed,
    }
  }

  private static convertShapeToCircleDto(
    shape: IGlobeViewCircle,
  ): IGlobeViewCircleInput {
    if (!shape) {
      return null
    }

    return {
      lineWidth: shape.lineWidth,
      lineColor: shape.lineColor,
      fillColor: shape.fillColor,
      fillOpacity: shape.fillOpacity,
      type: shape.type,
      isDisplayed: shape.isDisplayed,
    }
  }

  private static convertItemsToDtos<ShapeInputType>(
    items: IGlobeViewSpecificItemData[],
    convertShapeFn: (shape: IGlobeViewItemShapeInterface) => ShapeInputType,
  ): any[] {
    return items.map(i => {
      return {
        id: i.id,
        sitemapItemId: i.sitemapItemId,
        projectId: i.projectId,
        globeViewId: i.globeViewId,
        isHidden: i.isHidden,
        icon: GlobeView.convertToSitemapPinDto(i.icon),
        label: GlobeView.convertToTextBoxDto(i.label),
        shape: convertShapeFn(i.shape as IGlobeViewItemShapeInterface),
      }
    })
  }

  private static convertToSitemapPinDto(
    globeViewPin: IGlobeViewPin,
  ): IGlobeViewPinInput {
    if (!globeViewPin) {
      return null
    }

    return {
      isHidden: globeViewPin.isHidden,
    }
  }

  private static convertToTextBoxDto(
    textBox: IGlobeViewTextBox,
  ): IGlobeViewTextBoxInput {
    if (!textBox) {
      return null
    }

    return {
      fontSize: textBox.fontSize,
      isTextBoxDisplayed: textBox.isTextBoxDisplayed,
      isHidden: textBox.isHidden,
      color: textBox.color,
    }
  }

  public getCopy() {
    return new GlobeView(
      this.id,
      this.name,
      this.projectId,
      this.items || [],
      this.mapFiles || [],
      this.createdAt,
      this.updatedAt,
      this.bounds,
      this.center,
      this.zoom,
      this.altitude,
      this.pitch,
      this.bearing,
      this.geoCorners?.slice(),
      this.isOrientationLocked,
      this.filledImage,
      this.style,
    )
  }

  public getFullCopy(): GlobeView {
    const copy = this.getCopy()
    copy.updateFromJson(this.asJson)
    return copy
  }
}
